/**
 * taskFacts - the small derivations a task's chrome keeps re-deriving.
 *
 * Status colour, priority colour, the label for a status and how urgent a due
 * date is were each written out three times - once in `TaskDetail`'s header
 * badges, once in the Details tab's fact grid, and again the moment the
 * context pane wanted the same answers. Three copies is how the header comes
 * to say "amber" while the tile says "yellow".
 *
 * Pure functions, no React, no i18n instance: whoever calls passes `t`.
 */

import { parseServerDate } from '../../utils/functions';

export type TaskTone = 'default' | 'success' | 'warning' | 'danger';

type Translate = (key: string) => string;

/** Badge colour for a task status. */
export const taskStatusColor = (status?: string | null): string => {
  switch ((status || '').toLowerCase()) {
    case 'completed': return 'green';
    case 'in_progress': return 'blue';
    case 'active': return 'blue';
    case 'pending': return 'yellow';
    case 'todo': return 'gray';
    case 'deleted':
    case 'archived': return 'red';
    default: return 'gray';
  }
};

/** Human label for a task status. */
export const taskStatusLabel = (status: string | undefined | null, t: Translate): string => {
  switch ((status || '').toLowerCase()) {
    case 'completed': return t('Completed');
    case 'in_progress': return t('In progress');
    case 'todo': return t('To do');
    case 'archived': return t('Archived');
    case 'deleted': return t('Deleted');
    default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : t('To do');
  }
};

/** Badge colour for a task priority. */
export const taskPriorityColor = (priority?: string | null): string => {
  switch ((priority || '').toLowerCase()) {
    case 'low': return 'green';
    case 'medium': return 'yellow';
    case 'high': return 'amber';
    case 'critical': return 'red';
    default: return 'yellow';
  }
};

export interface TaskDueInfo {
  text: string;
  /**
   * overdue - the deadline has passed
   * soon    - due within 2 days
   * upcoming - due in 2 to 6 days
   * later   - 7 days or more away
   */
  urgency: 'overdue' | 'soon' | 'upcoming' | 'later';
}

const HOUR_MS = 1000 * 60 * 60;

/**
 * How close a due date is - or null when there is nothing to say (no date, or
 * the task is already done, when "Overdue" would be a lie).
 *
 * The bands are the due-date colour rule every task surface shares:
 * 2 days or less (overdue included) is red, 2 to 6 days is yellow, 7 days or
 * more is green.
 */
export const taskDueInfo = (
  dueDate: string | null | undefined,
  status: string | undefined,
  t: Translate
): TaskDueInfo | null => {
  if (!dueDate || (status || '').toLowerCase() === 'completed') return null;
  const due = parseServerDate(dueDate);
  if (!due) return null;
  const diffMs = due.getTime() - Date.now();
  const diffHours = diffMs / HOUR_MS;
  if (diffMs < 0) return { text: t('Overdue'), urgency: 'overdue' };
  if (diffHours <= 48) return { text: t('Due soon'), urgency: 'soon' };
  if (diffHours < 24 * 7) return { text: t('This week'), urgency: 'upcoming' };
  return { text: t('Upcoming'), urgency: 'later' };
};

/** The tone a due date lends the tile it sits in. */
export const taskDueTone = (info: TaskDueInfo | null): TaskTone => {
  switch (info?.urgency) {
    case 'overdue':
    case 'soon': return 'danger';
    case 'upcoming': return 'warning';
    case 'later': return 'success';
    default: return 'default';
  }
};

/** Badge colour for a due date - the same bands as `taskDueTone`. */
export const taskDueBadgeColor = (info: TaskDueInfo | null): 'red' | 'yellow' | 'green' | 'gray' => {
  switch (taskDueTone(info)) {
    case 'danger': return 'red';
    case 'warning': return 'yellow';
    case 'success': return 'green';
    default: return 'gray';
  }
};
